/**
 * The tabs a client sees INSIDE one project in the portal, and this header is
 * mostly about why they are not the same list as the portal's modules.
 *
 * `PORTAL_MODULES` is a route permission: it is the literal handed to
 * `scope.ownsFor(...)`, and `modules/portal/lib/gate.test.ts` asserts that every
 * such literal is a member of that list. A project tab is a different kind of
 * thing - it decides which panel of an ALREADY authorised project is drawn. It
 * is not a permission and it must never be mistaken for one.
 *
 * Merging the two lists would widen the set of strings that the drift gate
 * accepts as a permission from five to thirteen, and the day somebody wrote
 * `ownsFor("files", clientId)` the gate would go green over a check that means
 * nothing. Kept apart, that line is a build failure for free.
 *
 * ── THE `linked-` PREFIX IS LOAD-BEARING ───────────────────────────────────
 *
 * Four tabs show records that belong to a portal module. Naming them
 * "invoices" or "tickets" would make the two unions overlap as string literals,
 * so `ownsFor(tab, id)` would typecheck. The prefix makes the two vocabularies
 * provably disjoint, which `portalProjectTabs.test.ts` pins.
 *
 * ── A NAME ENTERS THIS LIST WITH THE READER BEHIND IT, NEVER BEFORE ────────
 *
 * An installation with no rule saved opens every name in the list. A tab added
 * ahead of its client-safe filter is therefore a tab that opens on the day it is
 * typed, showing whatever the CRM happens to hold behind it.
 */
import { type PortalModule } from "./portalModules.js";

/** Every project tab a client may be shown. Order is the tab order. */
export const PORTAL_PROJECT_TABS = [
  "overview",
  "milestones",
  "tasks",
  "files",
  "linked-invoices",
  "linked-estimates",
  "linked-contracts",
  "linked-tickets",
] as const;

export type PortalProjectTab = (typeof PORTAL_PROJECT_TABS)[number];

/**
 * What one level stores. Same three states as a module grant, same reasons:
 * `null` is "did not decide", `[]` is "decided on nothing".
 */
export type PortalProjectTabGrant = PortalProjectTab[] | null;

const TAB_SET: ReadonlySet<string> = new Set<string>(PORTAL_PROJECT_TABS);

export function isPortalProjectTab(value: unknown): value is PortalProjectTab {
  return typeof value === "string" && TAB_SET.has(value);
}

/**
 * Read a stored grant back into a value the page can use.
 *
 * Anything that is not an array is `null`, unrestricted - the same fail-open
 * rule as `parsePortalModules`, and for the same reason: a column holding
 * garbage must fail towards the behaviour every row had before the column
 * existed, not towards showing a paying client an empty project page.
 */
export function parsePortalProjectTabs(raw: unknown): PortalProjectTabGrant {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<PortalProjectTab>();
  for (const v of raw) if (isPortalProjectTab(v)) seen.add(v);
  return PORTAL_PROJECT_TABS.filter((t) => seen.has(t));
}

/**
 * Tabs that cannot be switched off.
 *
 * A project page with zero tabs draws a header and nothing under it, and the
 * `?sub=` fallback would have nowhere to land. Hiding the project page is done
 * by closing the `projects` MODULE, which is a different control in a different
 * place. The CRM editor draws this pill permanently on and disabled, so the
 * operator is told where the real switch is rather than left wondering why one
 * pill refuses to move.
 */
export const PORTAL_PROJECT_TAB_FLOOR: readonly PortalProjectTab[] = ["overview"];

/** The tabs a grant opens, in tab order, floor included. */
export function grantedProjectTabs(grant: PortalProjectTabGrant): PortalProjectTab[] {
  if (grant === null) return [...PORTAL_PROJECT_TABS];
  const on = new Set<PortalProjectTab>([...grant, ...PORTAL_PROJECT_TAB_FLOOR]);
  return PORTAL_PROJECT_TABS.filter((t) => on.has(t));
}

/** Does this grant open that tab? A `null` grant opens all of them. */
export function projectTabAllowed(grant: PortalProjectTabGrant, tab: PortalProjectTab): boolean {
  return grant === null || grant.includes(tab) || PORTAL_PROJECT_TAB_FLOOR.includes(tab);
}

/**
 * Two levels only, and there is deliberately no per-contact third.
 *
 * A project's panels are one company's arrangement of one piece of work, not a
 * per-person permission. Two people at the same company looking at the same
 * project must not see different panels of it, and a fourth level to keep in
 * step would buy a distinction nobody has asked for.
 */
export interface ProjectTabGrantLevels {
  global: PortalProjectTabGrant;
  client: PortalProjectTabGrant;
}

/** The client level replaces the default outright, exactly as modules do. */
export function resolveProjectTabGrant(levels: ProjectTabGrantLevels): PortalProjectTabGrant {
  return levels.client ?? levels.global;
}

/**
 * Which module a tab BORROWS its records from, or null when the tab owns them.
 *
 * This map is the rule "a project tab never widens a module grant". A tab with a
 * module here is drawn only when that module's gate also opens for this client,
 * and its records still come from that module's own gated list route. A tab with
 * null owns its data and its own grant is the only boundary there is.
 *
 * `portalProjectTabs.test.ts` requires every tab to have an entry, so a new tab
 * cannot fall into "owns its data" by omission - which is the generous
 * direction, and the wrong one to be generous in.
 */
export const PROJECT_TAB_MODULE: Record<PortalProjectTab, PortalModule | null> = {
  overview: null,
  milestones: null,
  tasks: null,
  files: null,
  "linked-invoices": "invoices",
  "linked-estimates": "estimates",
  "linked-contracts": "contracts",
  "linked-tickets": "support",
};
